import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { useEffect } from 'react';

import Container from '../components/Container';
import { getFirebaseAuth } from '../lib/firebase';

type Status = 'idle' | 'checking' | 'ready' | 'submitting' | 'success' | 'error' | 'no-code';

/**
 * `/reset` — the target of Firebase Auth's "reset password" action URL.
 *
 * URL shape:
 *   https://djinncoach.com/reset?mode=resetPassword&oobCode=ABC123&apiKey=...&lang=en
 *
 * Two-step flow:
 *   1. On mount, call verifyPasswordResetCode(oobCode) to validate the link
 *      and learn the user's email (which we display so they know whose
 *      password they're resetting).
 *   2. User submits a new password → confirmPasswordReset(oobCode, password).
 */
export default function Reset() {
  const [params] = useSearchParams();
  const oobCode = params.get('oobCode');
  const mode = params.get('mode');

  return <ResetLink key={JSON.stringify([oobCode, mode])} oobCode={oobCode} mode={mode} />;
}

interface ResetLinkProps { oobCode: string | null; mode: string | null }

function ResetLink({ oobCode, mode }: ResetLinkProps) {
  const active = useRef(false);
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);
  const [status, setStatus] = useState<Status>('idle');
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!oobCode || mode !== 'resetPassword') {
      setStatus('no-code');
      return;
    }
    const auth = getFirebaseAuth();
    if (!auth) {
      setStatus('error');
      setError('This page is misconfigured. Please contact support.');
      return;
    }
    setStatus('checking');
    let current = true;
    verifyPasswordResetCode(auth, oobCode)
      .then((addr) => {
        if (!current) return;
        setEmail(addr);
        setStatus('ready');
      })
      .catch(() => {
        if (!current) return;
        setStatus('error');
        setError('This password reset link has expired or has already been used.');
      });
    return () => { current = false; };
  }, [oobCode, mode]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (password.length < 8 || !/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/\d/.test(password)) {
      setError('Use at least 8 characters, including uppercase, lowercase, and a number.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    const auth = getFirebaseAuth();
    if (!auth || !oobCode) return;

    setStatus('submitting');
    try {
      await confirmPasswordReset(auth, oobCode, password);
      if (!active.current) return;
      setStatus('success');
    } catch (err) {
      if (!active.current) return;
      setStatus('ready');
      setError(
        err instanceof Error && err.message.includes('auth/weak-password')
          ? 'That password is too weak. Try a longer one.'
          : 'Could not reset your password. The link may have expired.',
      );
    }
  }

  return (
    <Container narrow>
      <div className="djinn-card p-8">
        <h1 className="text-2xl font-semibold tracking-tight text-djinn-text">
          Reset your password
        </h1>

        {status === 'checking' && (
          <p className="mt-4 text-djinn-text-2">Checking link…</p>
        )}

        {status === 'no-code' && (
          <p className="mt-4 text-djinn-text-2">
            This link is missing a reset code. Request a new one from the Djinn Coach app.
          </p>
        )}

        {status === 'success' && (
          <p className="mt-4 text-djinn-text-2">
            ✅ Your password has been reset. You can now sign in to Djinn Coach with your new
            password.
          </p>
        )}

        {(status === 'ready' || status === 'submitting') && (
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            {email && (
              <p className="text-sm text-djinn-text-muted">
                Resetting password for{' '}
                <span className="font-medium text-djinn-text">{email}</span>.
              </p>
            )}

            <label className="block">
              <span className="block text-sm font-medium text-djinn-text-2">
                New password
              </span>
              <input
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="djinn-input mt-1"
                autoComplete="new-password"
              />
            </label>

            <label className="block">
              <span className="block text-sm font-medium text-djinn-text-2">
                Confirm new password
              </span>
              <input
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="djinn-input mt-1"
                autoComplete="new-password"
              />
            </label>

            {error && <p className="text-sm text-djinn-danger">{error}</p>}

            <button
              type="submit"
              disabled={status === 'submitting'}
              className="djinn-btn-primary w-full"
            >
              {status === 'submitting' ? 'Resetting…' : 'Reset password'}
            </button>
          </form>
        )}

        {status === 'error' && <p className="mt-4 text-djinn-danger">{error}</p>}
      </div>
    </Container>
  );
}
